/**
 * tutorSettingsAdapter — le compte connecté → les pages des paramètres du tuteur, et le retour : ce
 * qu'une page enregistre → le corps de `authService.updateTutorAccount`.
 *
 * Port de la branche tuteur de `student/account/SettingsTabNavigator.js` (`dataTutor` de `onSubmit`,
 * le schéma `yup`) et de ses onglets partagés : `SettingsGeneralScreen` (prénom, nom, téléphone,
 * email verrouillé), `SettingsAddressScreen`, `SettingsConfigurationScreen`. Adresse et notifications
 * sont les mêmes formes que chez l'élève : leurs lectures viennent de `studentSettingsAdapter`.
 *
 * Comme l'ancien formulaire, chaque enregistrement envoie le corps ENTIER : les autres pages partent
 * avec les valeurs du compte.
 */
import { laravelFieldErrors } from "../../student/shared/laravelFieldErrors"
import { hasRole } from "../../student/Account/studentAccountAdapter"
import {
    isEmail,
    REQUIRED_MESSAGE,
    toAddressSettings,
    validateGeneralSettings,
    type ApiNotificationSettings,
    type ApiStudentSettingsUser,
} from "../../student/Account/studentSettingsAdapter"
import { type UIStudentAddressSettings } from "../../../ui/types/studentAccount"
import { type UITutorGeneralSettings } from "../../../ui/types/tutorCompte"

export type ApiTutorSettingsUser = Pick<
    ApiStudentSettingsUser,
    "roles" | "firstname" | "lastname" | "email" | "mobile" | "address" | "notification_settings"
> & {
    profile?: {
        professional_situation?: string | null
        study_field?: string | null
        current_position?: string | null
        motto?: string | null
    } | null
    students?: unknown[] | null
}

/** Les pages qui s'enregistrent. « Profil » ouvre « Compléter mon profil », il n'enregistre rien ici. */
export type TutorSettingsSection = "general" | "address" | "preferences"

export function toTutorGeneralSettings(user: ApiTutorSettingsUser): UITutorGeneralSettings {
    return {
        firstname: user.firstname ?? "",
        lastname: user.lastname ?? "",
        mobile: user.mobile ?? "",
        email: user.email ?? "",
    }
}

/**
 * « Général » : le schéma de l'ancien formulaire, pour ses quatre champs — les règles de l'élève
 * (prénom, nom, téléphone ; un tuteur n'a pas de parent), plus l'email, que le tuteur modifie et
 * doit donner.
 */
export function validateTutorGeneralSettings(
    values: UITutorGeneralSettings
): Partial<Record<keyof UITutorGeneralSettings, string>> {
    const errors: Partial<Record<keyof UITutorGeneralSettings, string>> = validateGeneralSettings({
        ...values,
        parentFirstname: "",
        parentLastname: "",
        parentMobile: "",
        parentEmail: "",
    })
    if (!values.email.trim()) errors.email = REQUIRED_MESSAGE
    else if (!isEmail(values.email)) errors.email = "Email invalide"
    return errors
}

export type TutorAccountEdits = {
    general?: UITutorGeneralSettings
    address?: UIStudentAddressSettings
    notificationSettings?: ApiNotificationSettings | null
}

/**
 * Le corps de `updateTutorAccount` — `dataTutor` de l'ancien `onSubmit`, champ pour champ : les quatre
 * du profil (situation, filière, poste, devise) repartent tels que le compte les porte, comme
 * l'ancien formulaire, qui les chargeait sans les montrer.
 */
export function toTutorAccountPayload(user: ApiTutorSettingsUser, edits: TutorAccountEdits = {}) {
    const general = edits.general ?? toTutorGeneralSettings(user)
    const address = edits.address ?? toAddressSettings({ address: user.address })
    const notificationSettings =
        edits.notificationSettings !== undefined ? edits.notificationSettings : (user.notification_settings ?? null)
    return {
        firstname: general.firstname,
        lastname: general.lastname,
        email: general.email,
        mobile: general.mobile,
        city: address.city,
        street: address.address,
        complement: address.addressComplement,
        postal_code: address.postalCode,
        country: address.countryId || "FR",
        professional_situation: user.profile?.professional_situation ?? undefined,
        study_field: user.profile?.study_field ?? undefined,
        current_position: user.profile?.current_position ?? undefined,
        motto: user.profile?.motto ?? undefined,
        notification_settings: notificationSettings,
    }
}

/** Le champ du corps → la page et le champ qui le montrent. */
const FIELD_BY_API_KEY: Record<string, [TutorSettingsSection, string]> = {
    firstname: ["general", "firstname"],
    lastname: ["general", "lastname"],
    email: ["general", "email"],
    mobile: ["general", "mobile"],
    street: ["address", "address"],
    complement: ["address", "addressComplement"],
    postal_code: ["address", "postalCode"],
    city: ["address", "city"],
    country: ["address", "countryId"],
}

/**
 * Les erreurs de champ d'un refus (422) : celles de la page enregistrée, sous leur champ. Un refus des
 * notifications n'a pas de champ à lui : il reste dans le message de la page Réglages. `elsewhere` :
 * un champ d'une autre page a été refusé — sa page ne s'affiche pas.
 */
export function tutorSettingsFieldErrors(
    error: unknown,
    section: TutorSettingsSection
): { fields: Record<string, string>; elsewhere: boolean } {
    const fields: Record<string, string> = {}
    let elsewhere = false
    for (const [key, message] of Object.entries(laravelFieldErrors(error))) {
        const apiKey = key.split(".")[0]
        if (apiKey === "notification_settings") {
            if (section !== "preferences") elsewhere = true
            continue
        }
        const target = FIELD_BY_API_KEY[apiKey]
        if (target && target[0] === section) {
            if (!fields[target[1]]) fields[target[1]] = message
        } else {
            elsewhere = true
        }
    }
    return { fields, elsewhere }
}

/** `DeleteAccountScreen.js` : un tuteur qui a encore des élèves ne ferme pas son compte lui-même. */
export const CLOSE_BLOCKED_WITH_STUDENTS =
    "Tu ne peux pas supprimer ton compte pour le moment, car tu es actuellement tuteur et tu as encore des élèves."

export function tutorHasStudents(user: ApiTutorSettingsUser | null | undefined): boolean {
    return hasRole(user, "tutor") && (user?.students?.length ?? 0) > 0
}
